/** @format */

import {
  Chain,
  Environment,
  MultipleProductsByEcodeParams,
  MultipleProductsByEcodeResponse,
  ProductDetailsByUrlResponse,
  ProductSkuCatalogDetails,
  PromotionDetailsMapV1,
  PromotionDetailsMapV2,
  PromotionResponseV1,
  PromotionResponseV2,
  SkuEDDResponse,
} from '@shared/data-access/interfaces';
import {
  CookieService,
  HttpService,
  KillswitchService,
  Logger,
} from '@shared/data-access/services';
import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { PDPRangeRequestBody } from './pdp-range-request-body.interface';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class ProductDao {
  private readonly chain = inject(Chain);
  private readonly environment = inject(Environment);
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly httpService = inject(HttpService);
  private readonly cookieService = inject(CookieService);
  private readonly logger = inject(Logger);
  private readonly killswitchService = inject(KillswitchService);
  private readonly isApiBrandDomainEnabled = this.killswitchService.killSwitchForCurrentChain(
    'enableSearchApiBrandDomain',
  );

  // TODO(enableSearchApiBrandDomain): once fully rolled out, drop this helper and always use seoByChain/withCredentials:true
  private getApiBrandDomainRequestConfig(
    path: string,
    httpHeaders: HttpHeaders,
    params: HttpParams,
  ): {
    url: string;
    options: { headers: HttpHeaders; params: HttpParams; withCredentials: boolean };
  } {
    const apiBrandDomainEnabled = this.isApiBrandDomainEnabled();
    const seoUrl = apiBrandDomainEnabled
      ? this.environment.api.seoByChain
      : this.environment.api.seo;

    return {
      url: `${seoUrl}${path}`,
      options: {
        headers: httpHeaders,
        params,
        withCredentials: apiBrandDomainEnabled,
      },
    };
  }

  /**
   * @description
   * Returns Promotion details for a given Product ID
   */
  public getPromotionsByProductId$(
    catEntryId: string | number,
    channelId: string,
    skuIdList?: string | string[],
    useNewPricingService?: boolean,
    retries = 0,
  ): Observable<PromotionDetailsMapV1 | PromotionDetailsMapV2> {
    const path = useNewPricingService ? 'v2' : 'v1';
    const url = `${this.environment.pricing.path}${path}/${this.chain.storeId}`;

    let httpParams = new HttpParams();
    httpParams = httpParams.set('catEntryId', `${catEntryId}`);
    httpParams = httpParams.set('channelId', channelId);

    if (useNewPricingService) {
      httpParams = httpParams.set('skus', `${skuIdList}`);
    }

    let httpHeaders = new HttpHeaders({
      Accept: MediaType.APPLICATION_JSON,
      'x-CentraSite-APIKey': this.environment.apiTokens.esb.api2,
      Env: this.environment.pricing.esbEnvironmentHeader,
    });

    // Get Origin Host Header
    const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
    if (origin) {
      httpHeaders = httpHeaders.set(CustomHttpHeaders.ORIGIN, origin);
    }

    // HTTP Options
    const httpOptions = {
      headers: httpHeaders,
      params: httpParams,
    };

    // Make the HTTP Request
    return this.http.get(url, httpOptions).pipe(
      map((response: PromotionResponseV1 | PromotionResponseV2) => {
        let promoDetailsMap: PromotionDetailsMapV1 | PromotionDetailsMapV2;

        if ((response as PromotionResponseV1)?.data?.promoDetailsMap) {
          promoDetailsMap = (response as PromotionResponseV1).data.promoDetailsMap;
        }

        if ((response as PromotionResponseV2)?.data?.discountDetailsMap) {
          promoDetailsMap = (response as PromotionResponseV2).data.discountDetailsMap;
        }

        // Return the raw data
        return promoDetailsMap;
      }),
      retry(retries),
    );
  }

  /**
   * @description
   * Submits user email and product details in order to set up a in stock reminder
   */
  public setProductInStockReminder$(
    userEmail: string,
    skuId: string,
    productTitle: string,
    productURL: string,
    imageURL: string,
    attributes: { label: string; value: string }[],
    storeNumber: string,
    storeName?: string,
    retries = 0,
  ): Observable<unknown> {
    // Standard HTTP Headers
    const httpHeaders = new HttpHeaders({
      Accept: MediaType.APPLICATION_JSON,
      partner_key: this.environment.apiTokens.inStockReminder.partner,
      secret_key: this.environment.apiTokens.inStockReminder.key,
    });
    const embededdedJsonBody = {
      title: productTitle,
      productURL,
      imageURL,
      attributes,
      chain: this.chain.chainIdentifierAbbr,
    };

    if (storeName) {
      embededdedJsonBody['storeName'] = storeName;
    }

    // Request body
    const reqBody = {
      sku: skuId,
      email: userEmail,
      storeNumber,
      embeddedJson: JSON.stringify(embededdedJsonBody),
    };

    if (storeNumber === undefined) {
      const storeCookies = {
        setStoreCookie: this.cookieService.get('setStoreCookie'),
        dcsg_ngx_cust_store: this.cookieService.get('DCSG_NGX_CUST_STORE'),
      };

      this.logger.log(storeCookies);
    }

    // HTTP Options
    const httpOptions = {
      headers: httpHeaders,
    };

    // Make the HTTP Request
    return this.http
      .post(
        `${this.environment.api.inStockReminder.baseUrl}v1/instockreminder`,
        reqBody,
        httpOptions,
      )
      .pipe(retry(retries));
  }

  /**
   * @description
   * Gets the Product details from the Product Quick View API
   */
  public getProductByUrl$(
    url: string,
    partNumber: string,
    channel: string,
    clearanceFlag?: string,
    antiClearanceFlag?: string,
    categoryId?: string,
    productParamType?: string,
    retries = 0,
  ): Observable<ProductDetailsByUrlResponse> {
    // Query String Parameters
    let httpParams = new HttpParams();

    // Set channel param
    httpParams = httpParams.set('channel', `${channel}`);

    // set inventory param
    httpParams = httpParams.set('inventory', 'true');

    // set active param
    httpParams = httpParams.set('active', 'true');

    // set active param
    httpParams = httpParams.set('skus', 'true');

    // Set url param
    httpParams = httpParams.set('url', `${url}`);

    // Set partNumber param
    httpParams = httpParams.set('partNumber', `${partNumber}`);

    // Check to see if the clearance flag is set
    if (typeof clearanceFlag !== 'undefined') {
      httpParams = httpParams.set('clearance', `${clearanceFlag}`);
    }

    // Check to see if the anticlearance flag is set
    if (typeof antiClearanceFlag !== 'undefined') {
      httpParams = httpParams.set('antiClearance', `${antiClearanceFlag}`);
    }

    // Check to see if category ID was provided
    if (typeof categoryId === 'string' && categoryId.trim() !== '') {
      httpParams = httpParams.set('categoryId', `${categoryId.trim()}`);
    }

    // Check to see if the parameter type has been specified
    if (typeof productParamType === 'string' && productParamType.trim() !== '') {
      httpParams = httpParams.set('searchType', `${productParamType.toLowerCase().trim()}`);
    }

    // Standard HTTP Headers
    let httpHeaders = new HttpHeaders({
      Accept: MediaType.APPLICATION_JSON,
    });

    // Custom HTTP Headers
    httpHeaders = httpHeaders.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');

    // Get Origin Host Header
    const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
    if (origin) {
      httpHeaders = httpHeaders.set(CustomHttpHeaders.ORIGIN, origin);
    }

    // Check platform
    if (!isPlatformBrowser(this.platformId)) {
      // Set a timeout on the server to prevent the response from taking too long
      httpHeaders = httpHeaders.set(CustomHttpHeaders.X_TIMEOUT, `${DCSG_CONSTANTS.timeouts.long}`);
    }

    // HTTP Options
    const httpOptions = {
      headers: httpHeaders,
      params: httpParams,
      withCredentials: true,
    };

    // Check to see if any of the optional parameters have been set
    if (httpParams.keys().length > 0) {
      // Add the Query String Parameters to the HTTP Options
      httpOptions.params = httpParams;
    }

    // Unlike getApiBrandDomainRequestConfig, this endpoint already fully cut over: no enableSearchApiBrandDomain check needed
    const seoUrl = this.environment.api.seoByChain;

    // Make the HTTP Request
    return this.http
      .get(`${seoUrl}catalog-productdetails/v4/byURL/${this.chain.storeId}`, httpOptions)
      .pipe(
        map((response: ProductDetailsByUrlResponse) => {
          // Return the raw data
          return response;
        }),
        retry(retries),
      );
  }

  public getProductByEcodeV4$(
    productId: string,
    checkClearance = false,
    retries = 0,
  ): Observable<ProductDetailsByUrlResponse> {
    let params = new HttpParams();
    params = params.set('id', productId).set('inventory', 'true').set('clearance', checkClearance);

    let httpHeaders = new HttpHeaders({
      Accept: MediaType.APPLICATION_JSON,
    });

    // Get Origin Host Header
    const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
    if (origin) {
      httpHeaders = httpHeaders.set(CustomHttpHeaders.ORIGIN, origin);
    }

    const { url, options } = this.getApiBrandDomainRequestConfig(
      `catalog-productdetails/v4/byPartNumber/${this.chain.storeId}`,
      httpHeaders,
      params,
    );

    return this.http.get<ProductDetailsByUrlResponse>(url, options).pipe(retry(retries));
  }

  /**
   * Get the Sku EDD Data
   * @param skuId - SkuId
   * @returns - Sku EDD Data
   */
  public getSkuDeliveryDate$(skuId: string): Observable<SkuEDDResponse> {
    // Standard HTTP Headers
    const httpHeaders = new HttpHeaders({
      Accept: MediaType.APPLICATION_JSON,
      'Content-Type': MediaType.APPLICATION_JSON,
      'Access-Control-Allow-Origin': '*',
      Pragma: 'no-cache',
      'X-TIMEOUT': DCSG_CONSTANTS.timeouts.short,
    });

    // Request body.
    const reqBody: PDPRangeRequestBody = {
      consumingTeam: 'PDP',
      referer: window.location.href.split('?')[0],
      source: this.chain.metaCanonical,
      customerZipCode:
        this.cookieService.get('deliveryZip') || this.cookieService.get('whereabouts'),
      skus: [skuId],
    };

    // HTTP Options
    const httpOptions = {
      headers: httpHeaders,
    };

    // Make the HTTP Request
    return this.http
      .post(`${this.environment.api.edd.proxy.path}v2/edd-pdp/ranges`, reqBody, httpOptions)
      .pipe(
        map((response: SkuEDDResponse) => response),
        catchError(() => of(null)),
      );
  }

  public getMultipleProductsByEcode$<
    T extends MultipleProductsByEcodeParams | undefined = undefined,
  >(ecodes: string[], options?: T, retries = 0) {
    let params = new HttpParams();
    for (const param in options || {}) {
      params = params.set(param, options[param]);
    }
    params = params.set('ecode', ecodes.join(','));

    let httpHeaders = new HttpHeaders({
      Accept: MediaType.APPLICATION_JSON,
    });

    // Get Origin Host Header
    const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
    if (origin) {
      httpHeaders = httpHeaders.set(CustomHttpHeaders.ORIGIN, origin);
    }

    const requestConfig = this.getApiBrandDomainRequestConfig(
      `catalog-productdetails/v4/store/${this.chain.storeId}/products`,
      httpHeaders,
      params,
    );

    return this.http
      .get<MultipleProductsByEcodeResponse<T>>(requestConfig.url, requestConfig.options)
      .pipe(retry(retries));
  }

  public getProductsBySkus$(skus: string[], retries = 0): Observable<ProductSkuCatalogDetails[]> {
    let params = new HttpParams();
    params = params.set('id', skus.join(','));

    let httpHeaders = new HttpHeaders({
      Accept: MediaType.APPLICATION_JSON,
    });

    // Get Origin Host Header
    const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
    if (origin) {
      httpHeaders = httpHeaders.set(CustomHttpHeaders.ORIGIN, origin);
    }

    const { url, options } = this.getApiBrandDomainRequestConfig(
      `catalog-productdetails/v4/bySku/${this.chain.storeId}`,
      httpHeaders,
      params,
    );

    return this.http.get<ProductSkuCatalogDetails[]>(url, options).pipe(retry(retries));
  }
}
